export default class Rating {

    waveheight = 0
    swellperiod = 0
    swelldirection = 0
    winddirection = 0
    windspeed = 0
    points = 0
    maxPoints = 15
    score = 0.0
    maxScore = 5.0

    constructor(conditions = {}, ideal = {}){        
        this.conditions = conditions;
        this.ideal = ideal;  
        this.calculate()
    }

    // Update conditions and recalculate
    updateConditions(conditions) {
        this.conditions = conditions;
        this.calculate();
    }

    // Update ideal preferences and recalculate
    updateIdeal(ideal) {
        this.ideal = ideal;
        this.calculate();
    }

    // Update both and recalculate
    update(conditions, ideal) {
        this.conditions = conditions;
        this.ideal = ideal;
        this.calculate();
    }

    // Calculate all ratings
    calculate() {
        this.getWaveHeightRating()  
        this.getSwellPeriodRating() 
        this.getWindSpeedRating()
        this.getSwellDirectionRating()   
        this.getWindDirectionRating()
        this.points = this.waveheight + this.swellperiod + this.windspeed + this.swelldirection + this.winddirection
        this.getScore()
    }

    // Get rating out of 5
    getScore() {
        this.score = Number(((this.points / this.maxPoints) * 5).toFixed(1));
    }

    getWaveHeightRating(){
        
        const waveheight = this.conditions.waveheight;
        const min = this.ideal.preferred_wave_height_min;
        const max = this.ideal.preferred_wave_height_max;

        // Check for null/undefined BEFORE converting
        if(waveheight === null || waveheight === undefined || 
           min === null || min === undefined || 
           max === null || max === undefined){
            this.waveheight = 0;
            return;
        }

        // Convert to numbers after null check
        const wave = Number(waveheight);
        const minWave = Number(min);
        const maxWave = Number(max);

        if(wave >= minWave && wave <= maxWave){
            this.waveheight = 3
        } else if(wave < minWave && wave >= minWave - 0.2){
            this.waveheight = 2
        } else if(wave > maxWave && wave <= maxWave + 0.2){
            this.waveheight = 2
        } else if(wave < minWave - 0.2 && wave >= minWave - 0.4){
            this.waveheight = 1
        } else if(wave > maxWave + 0.2 && wave <= maxWave + 0.4){
            this.waveheight = 1
        } else {
            this.waveheight = 0
        }
    }

    getSwellPeriodRating(){
        
        const swellperiod = this.conditions.swellperiod;
        const min = this.ideal.preferred_swell_period_min;
        const max = this.ideal.preferred_swell_period_max;
        const swelldirection = this.conditions.swelldirection;
        const idealSwellDir = this.ideal.preferred_swell_direction;

        // Check for null/undefined BEFORE converting
        if(swellperiod === null || swellperiod === undefined || 
           min === null || min === undefined || 
           max === null || max === undefined){
            this.swellperiod = 0;
            return;
        }

        // Convert to numbers after null check
        const period = Number(swellperiod);
        const minPeriod = Number(min);
        const maxPeriod = Number(max);

        // Check if swell direction is way off (more than 45 degrees)
        let isDirectionBad = false;
        if(swelldirection !== null && swelldirection !== undefined && 
           idealSwellDir !== null && idealSwellDir !== undefined){
            const swellDir = Number(swelldirection);
            const idealDir = Number(idealSwellDir);
            
            let diff = Math.abs(swellDir - idealDir);
            if (diff > 180) {
                diff = 360 - diff;
            }
            
            // If direction is more than 45 degrees off, it's bad
            if(diff > 45){
                isDirectionBad = true;
            }
        }

        // If direction is bad, penalize the swell period score
        if(isDirectionBad){
            // Even if period is ideal, bad direction means poor score
            if(period >= minPeriod && period <= maxPeriod){
                this.swellperiod = 1  // Reduced from 3 to 1
            } else if(period < minPeriod && period >= minPeriod - 1){
                this.swellperiod = 0  // Reduced from 2 to 0
            } else if(period > maxPeriod && period <= maxPeriod + 1){
                this.swellperiod = 0  // Reduced from 2 to 0
            } else {
                this.swellperiod = 0
            }
        } else {
            // Normal scoring when direction is acceptable
            if(period >= minPeriod && period <= maxPeriod){
                this.swellperiod = 3
            } else if(period < minPeriod && period >= minPeriod - 1){
                this.swellperiod = 2
            } else if(period > maxPeriod && period <= maxPeriod + 1){
                this.swellperiod = 2
            } else if(period < minPeriod - 1 && period >= minPeriod - 2){
                this.swellperiod = 1
            } else if(period > maxPeriod + 1 && period <= maxPeriod + 2){
                this.swellperiod = 1
            } else {
                this.swellperiod = 0
            }
        }
    }

    getWindSpeedRating(){
        
        const windspeed = this.conditions.windspeed;
        const min = this.ideal.preferred_wind_speed_min;
        const max = this.ideal.preferred_wind_speed_max;
        const winddirection = this.conditions.winddirection;
        const idealWindDir = this.ideal.preferred_wind_direction;

        // Check for null/undefined BEFORE converting
        if(windspeed === null || windspeed === undefined || 
           min === null || min === undefined || 
           max === null || max === undefined){
            this.windspeed = 0;
            return;
        }

        // Convert to numbers after null check
        const speed = Number(windspeed);
        const minSpeed = Number(min);
        const maxSpeed = Number(max);

        // Check if wind direction is way off (more than 95 degrees)
        let isDirectionBad = false;
        if(winddirection !== null && winddirection !== undefined && 
           idealWindDir !== null && idealWindDir !== undefined){
            const windDir = Number(winddirection);
            const idealDir = Number(idealWindDir);
            
            let diff = Math.abs(windDir - idealDir);
            if (diff > 180) {
                diff = 360 - diff;
            }
            
            // If direction is more than 95 degrees off, it's bad
            if(diff > 95){
                isDirectionBad = true;
            }
        }

        // If direction is bad, penalize the wind speed score heavily
        if(isDirectionBad){
            // Even if speed is ideal, bad direction means poor score
            if(speed >= minSpeed && speed <= maxSpeed){
                this.windspeed = 1  // Reduced from 3 to 1
            } else if(speed < minSpeed && speed >= minSpeed - 2){
                this.windspeed = 0  // Reduced from 2 to 0
            } else if(speed > maxSpeed && speed <= maxSpeed + 2){
                this.windspeed = 0  // Reduced from 2 to 0
            } else {
                this.windspeed = 0
            }
        } else {
            // Normal scoring when direction is acceptable
            if(speed >= minSpeed && speed <= maxSpeed){
                this.windspeed = 3
            } else if(speed < minSpeed && speed >= minSpeed - 2){
                this.windspeed = 2
            } else if(speed > maxSpeed && speed <= maxSpeed + 2){
                this.windspeed = 2
            } else if(speed < minSpeed - 2 && speed >= minSpeed - 5){
                this.windspeed = 1
            } else if(speed > maxSpeed + 2 && speed <= maxSpeed + 5){
                this.windspeed = 1
            } else {
                this.windspeed = 0
            }
        }
    }

    getSwellDirectionRating(){
        const swelldirection = this.conditions.swelldirection;
        const ideal = this.ideal.preferred_swell_direction;

        // Check for null/undefined BEFORE converting to Number
        if(swelldirection === null || swelldirection === undefined || 
           ideal === null || ideal === undefined){
            this.swelldirection = 0;
            return;
        }

        // Convert to numbers after null check
        const swellDir = Number(swelldirection);
        const idealDir = Number(ideal);

        // Calculate the difference between directions
        let diff = Math.abs(swellDir - idealDir);
        
        // Handle wrapping around 360 degrees (e.g., 350 and 10 are only 20 degrees apart)
        if (diff > 180) {
            diff = 360 - diff;
        }

        if(diff <= 5){
            this.swelldirection = 3
        } else if(diff <= 20){
            this.swelldirection = 2
        } else if(diff <= 45){  
            this.swelldirection = 1
        } else {
            this.swelldirection = 0
        }
    }
    
    getWindDirectionRating(){
        const winddirection = this.conditions.winddirection;
        const ideal = this.ideal.preferred_wind_direction;
        const windspeed = this.conditions.windspeed;

        // Check for null/undefined BEFORE converting to Number
        if(winddirection === null || winddirection === undefined || 
           ideal === null || ideal === undefined){
            this.winddirection = 0;
            return;
        }

        // Convert to numbers after null check
        const windDir = Number(winddirection);
        const idealDir = Number(ideal);
        const speed = Number(windspeed || 0); // Default to 0 if windspeed is null

        // Calculate the difference between directions
        let diff = Math.abs(windDir - idealDir);
        
        // Handle wrapping around 360 degrees (e.g., 350 and 10 are only 20 degrees apart)
        if (diff > 180) {
            diff = 360 - diff;
        }

        if(speed <= 8){
            // For low/no wind, direction doesn't matter much - give full points
            this.winddirection = 3
        } else if(diff <= 20){
            this.winddirection = 3
        } else if(diff <= 45){
            this.winddirection = 2
        } else if(diff <= 80){
            this.winddirection = 1
        } else {
            // More than 80 degrees off - this is bad
            this.winddirection = 0
        }
    }
}